import Vue from 'vue';
import { transform, isEqual, cloneDeepWith, cloneDeep, omit } from 'lodash';
import { VNode, VNodeDirective } from 'vue/types';
import moment from 'moment';
import { AxiosError } from 'axios';
import { FieldErrors, SystemError } from '@/models/error/error';
import { ISystemErrorData } from '@/models/error/error.types';

export type Dictionary<T> = { [key: string]: T };

export const BYTES_PER_KILOBYTES = 1024;
export const KILOBYTES_PER_MEGABYTES = BYTES_PER_KILOBYTES;
export const BYTES_PER_MEGABYTES =
  BYTES_PER_KILOBYTES * KILOBYTES_PER_MEGABYTES;

export function createSimpleFunctional(c: string, el = 'div', name?: string) {
  return Vue.extend({
    name: name || c.replace(/__/g, '-'),

    functional: true,

    render(h, { data, children }): VNode {
      data.staticClass = `${c} ${data.staticClass || ''}`.trim();

      return h(el, data, children);
    }
  });
}

export type BindingConfig = Pick<VNodeDirective, 'arg' | 'modifiers' | 'value'>;
export function directiveConfig(
  binding: BindingConfig,
  defaults = {}
): VNodeDirective {
  return {
    ...defaults,
    ...binding.modifiers,
    value: binding.arg,
    ...(binding.value || {})
  };
}

export function addOnceEventListener(
  el: EventTarget,
  eventName: string,
  cb: (event: Event) => void,
  options: boolean | AddEventListenerOptions = false
): void {
  const once = (event: Event) => {
    cb(event);
    el.removeEventListener(eventName, once, options);
  };

  el.addEventListener(eventName, once, options);
}

export function getNestedValue(
  obj: any,
  path: (string | number)[],
  fallback?: any
): any {
  const last = path.length - 1;

  if (last < 0) return obj === undefined ? fallback : obj;

  for (let i = 0; i < last; i++) {
    if (obj == null) {
      return fallback;
    }
    obj = obj[path[i]];
  }

  if (obj == null) return fallback;

  return obj[path[last]] === undefined ? fallback : obj[path[last]];
}

export function deepEqual(a: any, b: any): boolean {
  if (a === b) return true;

  if (a instanceof Date && b instanceof Date) {
    // If the values are Date, they were convert to timestamp with getTime and compare it
    if (a.getTime() !== b.getTime()) return false;
  }

  if (a !== Object(a) || b !== Object(b)) {
    // If the values aren't objects, they were already checked for equality
    return false;
  }

  const props = Object.keys(a);

  if (props.length !== Object.keys(b).length) {
    // Different number of props, don't bother to check
    return false;
  }

  return props.every(p => deepEqual(a[p], b[p]));
}

export function getObjectValueByPath(
  obj: any,
  path: string,
  fallback?: any
): any {
  // credit: http://stackoverflow.com/questions/6491463/accessing-nested-javascript-objects-with-string-key#comment55278413_6491621
  if (obj == null || !path || typeof path !== 'string') return fallback;
  if (obj[path] !== undefined) return obj[path];
  path = path.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
  path = path.replace(/^\./, ''); // strip a leading dot
  return getNestedValue(obj, path.split('.'), fallback);
}

export type SelectItemKey<T = Record<string, any>> =
  | boolean
  | null
  | undefined // Ignored
  | string // Lookup by key, can use dot notation for nested objects
  | readonly (string | number)[] // Nested lookup by key, each array item is a key in the next level
  | ((item: T, fallback?: any) => any);

export function getPropertyFromItem(
  item: any,
  property: SelectItemKey,
  fallback?: any
): any {
  if (property == null) return item === undefined ? fallback : item;

  if (item !== Object(item)) return fallback === undefined ? item : fallback;

  if (typeof property === 'string')
    return getObjectValueByPath(item, property, fallback);

  if (Array.isArray(property)) return getNestedValue(item, property, fallback);

  if (typeof property !== 'function') return fallback;

  const value = property(item, fallback);

  return typeof value === 'undefined' ? fallback : value;
}

export function createRange(length: number): number[] {
  return Array.from({ length }, (v, k) => k);
}

export function getZIndex(el?: Element | null): number {
  if (!el || el.nodeType !== Node.ELEMENT_NODE) return 0;

  const index = +window.getComputedStyle(el).getPropertyValue('z-index');

  if (!index) return getZIndex(el.parentNode as Element);
  return index;
}

const tagsToReplace = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;'
} as any;

export function escapeHTML(str: string): string {
  return str.replace(/[&<>]/g, tag => tagsToReplace[tag] || tag);
}

export function filterObjectOnKeys<T, K extends keyof T>(
  obj: T,
  keys: K[]
): { [N in K]: T[N] } {
  const filtered = {} as { [N in K]: T[N] };

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (typeof obj[key] !== 'undefined') {
      filtered[key] = obj[key];
    }
  }

  return filtered;
}

export function convertToUnit(
  str: string | number | null | undefined,
  unit = 'px'
): string | undefined {
  if (str == null || str === '') {
    return undefined;
  } else if (isNaN(+str!)) {
    return String(str);
  } else {
    return `${Number(str)}${unit}`;
  }
}

export function kebabCase(str: string): string {
  return (str || '').replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}

export function isObject<T extends object>(input: any): input is T {
  return input !== null && !isArray(input) && typeof input === 'object';
}
export function isString<T extends string>(input: any): input is T {
  return input !== null && typeof input === 'string';
}
export function isEmptyObject(obj: any) {
  return (
    toString.call(obj) === '[object Object]' && Object.keys(obj).length === 0
  );
}
export const isArray = (arr: any) => arr !== null && Array.isArray(arr);

// KeyboardEvent.keyCode aliases
export const keyCodes = Object.freeze({
  enter: 13,
  tab: 9,
  delete: 46,
  esc: 27,
  space: 32,
  up: 38,
  down: 40,
  left: 37,
  right: 39,
  end: 35,
  home: 36,
  del: 46,
  backspace: 8,
  insert: 45,
  pageup: 33,
  pagedown: 34
});

// This remaps internal names like '$cancel' or '$vuetify.icons.cancel'
// to the current name or component for that icon.
// export function remapInternalIcon(vm: Vue, iconName: string): VuetifyIcon {
//   if (!iconName.startsWith('$')) {
//     return iconName;
//   }

//   // Get the target icon name
//   const iconPath = `$vuetify.icons.values.${iconName
//     .split('$')
//     .pop()!
//     .split('.')
//     .pop()}`;

//   // Now look up icon indirection name,
//   // e.g. '$vuetify.icons.values.cancel'
//   return getObjectValueByPath(vm, iconPath, iconName);
// }

/**
 * Camelize a hyphen-delimited string.
 */
const camelizeRE = /-(\w)/g;
export const camelize = (str: string): string => {
  return str.replace(camelizeRE, (_, c) => (c ? c.toUpperCase() : ''));
};

/**
 * Makes the first character of a string uppercase
 */
export function upperFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function groupItems<T = any>(
  items: T[],
  groupBy: string[]
): Record<string, T[]> {
  const key = groupBy[0];
  return items.reduce((rv: any, x: any) => {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {} as Record<string, T[]>);
}

export function wrapInArray<T>(v: T | T[] | null | undefined): T[] {
  return v != null ? (Array.isArray(v) ? v : [v]) : [];
}

export function sortItems<T = any>(
  items: T[],
  sortBy: string[],
  sortDesc: boolean[],
  locale: string,
  customSorters?: any
): T[] {
  if (sortBy === null || !sortBy.length) return items;
  const stringCollator = new Intl.Collator(locale, {
    sensitivity: 'accent',
    usage: 'sort'
  });

  return items.sort((a, b) => {
    for (let i = 0; i < sortBy.length; i++) {
      const sortKey = sortBy[i];

      let sortA = getObjectValueByPath(a, sortKey);
      let sortB = getObjectValueByPath(b, sortKey);

      if (sortDesc[i]) {
        [sortA, sortB] = [sortB, sortA];
      }

      if (customSorters && customSorters[sortKey]) {
        const customResult = customSorters[sortKey](sortA, sortB);

        if (!customResult) continue;

        return customResult;
      }

      // Check if both cannot be evaluated
      if (sortA === null && sortB === null) {
        continue;
      }

      [sortA, sortB] = [sortA, sortB].map(s =>
        (s || '').toString().toLocaleLowerCase()
      );

      if (sortA !== sortB) {
        if (!isNaN(sortA) && !isNaN(sortB))
          return Number(sortA) - Number(sortB);
        return stringCollator.compare(sortA, sortB);
      }
    }

    return 0;
  });
}

export function defaultFilter(value: any, search: string | null, item?: any) {
  item;
  return (
    value != null &&
    search != null &&
    typeof value !== 'boolean' &&
    value
      .toString()
      .toLocaleLowerCase()
      .indexOf(search.toLocaleLowerCase()) !== -1
  );
}

export function searchItems<T = any>(items: T[], search: string): T[] {
  if (!search) return items;
  search = search.toString().toLowerCase();
  if (search.trim() === '') return items;
  // search = typeof search === 'string' ? search.trim() : null

  return items.filter((item: any) =>
    Object.keys(item).some(key =>
      defaultFilter(getObjectValueByPath(item, key), search, item)
    )
  );
}

/**
 * Returns:
 *  - 'normal' for old style slots - `<template slot="default">`
 *  - 'scoped' for old style scoped slots (`<template slot="default" slot-scope="data">`) or bound v-slot (`#default="data"`)
 *  - 'v-slot' for unbound v-slot (`#default`) - only if the third param is true, otherwise counts as scoped
 */
export function getSlotType<T extends boolean = false>(
  vm: Vue,
  name: string,
  split?: T
): (T extends true ? 'v-slot' : never) | 'normal' | 'scoped' | void {
  if (
    vm.$slots[name] &&
    vm.$scopedSlots[name] &&
    (vm.$scopedSlots[name] as any).name
  ) {
    return split ? ('v-slot' as any) : 'scoped';
  }
  if (vm.$slots[name]) return 'normal';
  if (vm.$scopedSlots[name]) return 'scoped';
}

export function getPrefixedScopedSlots(prefix: string, scopedSlots: any) {
  return Object.keys(scopedSlots)
    .filter(k => k.startsWith(prefix))
    .reduce((obj: any, k: string) => {
      obj[k.replace(prefix, '')] = scopedSlots[k];
      return obj;
    }, {});
}

export function getSlot(
  vm: Vue,
  name = 'default',
  data?: object | (() => object),
  optional = false
) {
  if (vm.$scopedSlots[name]) {
    return vm.$scopedSlots[name]!(data instanceof Function ? data() : data);
  } else if (vm.$slots[name] && (!data || optional)) {
    return vm.$slots[name];
  }
  return undefined;
}

export function clamp(value: number, min = 0, max = 1) {
  return Math.max(min, Math.min(max, value));
}

export function padEnd(str: string, length: number, char = '0') {
  return str + char.repeat(Math.max(0, length - str.length));
}

export function chunk(str: string, size = 1) {
  const chunked: string[] = [];
  let index = 0;
  while (index < str.length) {
    chunked.push(str.substr(index, size));
    index += size;
  }
  return chunked;
}

export function humanReadableFileSize(bytes: number, binary = false): string {
  const base = binary ? 1024 : 1000;
  if (bytes < base) {
    return `${bytes} B`;
  }

  const prefix = binary ? ['Ki', 'Mi', 'Gi'] : ['k', 'M', 'G'];
  let unit = -1;
  while (Math.abs(bytes) >= base && unit < prefix.length - 1) {
    bytes /= base;
    ++unit;
  }
  return `${bytes.toFixed(1)} ${prefix[unit]}B`;
}

export function camelizeObjectKeys(
  obj: Record<string, any> | null | undefined
) {
  if (!obj) return {};

  return Object.keys(obj).reduce((o: any, key: string) => {
    o[camelize(key)] = obj[key];
    return o;
  }, {});
}

export function mergeDeep(
  source: Dictionary<any> = {},
  target: Dictionary<any> = {}
) {
  for (const key in target) {
    const sourceProperty = source[key];
    const targetProperty = target[key];

    // Only continue deep merging if
    // both properties are objects
    if (isObject(sourceProperty) && isObject(targetProperty)) {
      source[key] = mergeDeep(sourceProperty, targetProperty);

      continue;
    }

    source[key] = targetProperty;
  }

  return source;
}

export function isNumber(n: any): n is number {
  return !isNaN(parseFloat(n)) && isFinite(n);
}

export function checkEmpty(obj: any) {
  for (const key in obj) {
    if (obj[key] instanceof Object === true) {
      if (checkEmpty(obj[key]) === false) return false;
    } else {
      if (obj[key].length !== 0) return false;
    }
  }
  return true;
}

export const findExistingIndex = (
  arr: any[],
  item: object,
  property: any,
  fallback?: any
) => {
  const itemValue = getPropertyFromItem(item, property, fallback);
  return (arr || []).findIndex(i =>
    deepEqual(getPropertyFromItem(i, property, fallback), itemValue)
  );
};

export function filterDuplicates<T extends Record<string | number, any>>(
  arr: T[],
  property: SelectItemKey<Record<string, any>>,
  fallback?: any
): T[] {
  const uniqueValues = new Map();

  for (let index = 0; index < arr.length; ++index) {
    const item = arr[index];
    const val = getPropertyFromItem(item, property, fallback); // TODO: comparator

    !uniqueValues.has(val) && uniqueValues.set(val, item);
  }

  return Array.from(uniqueValues.values());
}

/** determine if color processed is avaible for css*/
export function isCssColor(color?: string | false): boolean {
  return !!color && !!color.match(/^(#|var\(--|(rgb|hsl)a?\()/);
}

interface Color {
  r: number;
  g: number;
  b: number;
}
export function parseColor(color: string | object): any {
  if (isObject(color)) {
    return color as Color;
  }

  const match = /#(\w\w)(\w\w)(\w\w)/.exec(color);

  if (!match) {
    // throw 'Colors must be in the format of #rrggbb';
    console.warn('Colors must be in the format of #rrggbb');
    return false;
    // throw new Error("Colors must be in the format of #rrggbb");
  }

  return {
    r: parseInt(match[1], 16),
    g: parseInt(match[2], 16),
    b: parseInt(match[3], 16)
  };
}

/**
 * Creates an object composed of keys generated from the results of running each element of collection thru iteratee.
 * The order of grouped values is determined by the order they occur in collection.
 * The corresponding value of each key is an array of elements responsible for generating the key.
 * The iteratee is invoked with one argument: (value).
 *
 * @param {T[]} objectArray
 * @param {keyof T } property
 * @return {Dictionary<T[]>} Returns the composed aggregate object.
 * @template T
 *
 * @todo TODO: 2021-01-17 - LH -  look into libraries for replacement implementation e.g: 'lodash'
 */
export function groupBy<T>(
  objectArray: T[],
  property: keyof T
): Dictionary<T[]> {
  return objectArray.reduce((acc, obj) => {
    const key = obj[property];
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(obj);
    return acc;
  }, {} as any);
}

// TODO: LF-2020-01-16 - Check with LeRon if there is standard way to do this
export function omitDeep<T>(collection: T, excludeKeys: any[]) {
  function omitFn(value: { [x: string]: any }) {
    if (value && typeof value === 'object') {
      excludeKeys.forEach(key => {
        delete value[key];
      });
    }
  }

  return cloneDeepWith<T>(collection, omitFn) as T;
}

/**
 * Deep diff between two object, using lodash
 * @param  {Object} object Object compared
 * @param  {Object} base   Object to compare with
 * @param  {Boolean} includeId flag to control including ID with difference
 * @return {Object}        Return a new object who represent the diff
 */
export function diffDeep<T>(object: T, base: any, includeId = false): T {
  function changes(object: any, base: any) {
    return transform(object, function(result: any, value, key) {
      if (!isEqual(value, base[key])) {
        if (Array.isArray(value) && Array.isArray(base[key])) {
          result[key] = [];

          // loop through each entry of array
          for (let i = 0; i < value.length; i++) {
            if (isObject(value[i])) {
              const base_entry = base[key].find(
                (data: { id?: any }) => data?.id === value[i]?.id
              );

              if (base_entry) {
                if (!isEqual(value[i], base_entry)) {
                  // get the differences
                  result[key].push(changes(value[i], base_entry));
                } else {
                  // We always need to include ID for array entries
                  // even if there was no change - otherwise item
                  // gets deleted when data written to database
                  result[key].push({ id: value[i].id });
                }
              } else {
                result[key].push(value[i]);
              }
            } else {
              result[key].push(value[i]);
            }
          }
        } else {
          result[key] =
            isObject(value) && isObject(base[key])
              ? changes(value, base[key])
              : value;

          // include ID field if applicable
          if (includeId) {
            result['id'] = base['id'];
          }
        }
      } else {
        // save id regardless even if equal
        if (includeId) {
          result['id'] = base['id'];
        }
      }
    });
  }
  return changes(object, base);
}

export function compareOrder(otherArray: any) {
  return function(current: any) {
    return (
      otherArray.filter(function(other: any) {
        return other.id == current.id && other.order == current.order;
      }).length == 0
    );
  };
}

export function mask(value: string, dp: number, editing: boolean) {
  return editing ? value : parseFloat(value).toFixed(dp);
}

export function scrollToBottomOfHtmlElement(element?: string | HTMLElement) {
  const scroll = $(element);
  const targetLocation = scroll?.scrollHeight ?? scroll?.clientHeight;

  const startTime = performance.now();
  const startLocation = scroll?.scrollTop;

  // if targetlocation is the same startlocation
  if (targetLocation === startLocation) return Promise.resolve(targetLocation);

  const duration = 1000; // 1 second

  // Scroll behaviour for easing animation
  /** @see import * as easings from 'vuetify/lib/services/goto/easing-patterns' */
  const easeInOutCubic = (t: number) =>
    t < 0.5 ? 4 * t ** 3 : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;

  return new Promise(resolve =>
    requestAnimationFrame(function step(currentTime: number) {
      const timeElapsed = currentTime - startTime;

      // decrementing progress as end condition for scroll
      const progress = Math.abs(
        duration ? Math.min(timeElapsed / duration, 1) : 1
      );

      // set new height to be the difference between location multiplied by animation offset
      const top = Math.floor(
        startLocation! +
          (targetLocation! - startLocation!) * easeInOutCubic(progress)
      );

      //set the scroll top to be the new top value per frame
      scroll?.scrollTo({
        top
      });

      //if element scroll top has not fully loaded then use the document client height
      const clientHeight =
        scroll?.clientHeight ?? document.documentElement.clientHeight;

      if (
        progress === 1 ||
        clientHeight + scroll!.scrollTop === scroll?.scrollHeight
      ) {
        return resolve(targetLocation);
      }
      // run requestAnimationFrame if functions has not returned
      requestAnimationFrame(step);
    })
  );
}

export function $(el: any): HTMLElement | null {
  if (typeof el === 'string') {
    return document.querySelector<HTMLElement>(el);
  } else if (el && el._isVue) {
    return (el as Vue).$el as HTMLElement;
  } else if (el instanceof HTMLElement) {
    return el;
  } else {
    return null;
  }
}

export function roundCurrency(num: number) {
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

/**
 * Validates that a string is a valid web URL.
 *
 * @param {any} urlStr The string to validate.
 * @return {boolean} Whether the string is valid web URL or not.
 */
export function isURL(urlStr: any): boolean {
  if (typeof urlStr !== 'string') {
    return false;
  }
  // Lookup illegal characters.
  const re = /[^a-z0-9:/?#[\]@!$&'()*+,;=.\-_~%]/i;
  if (re.test(urlStr)) {
    return false;
  }
  try {
    const uri = new URL(urlStr);
    const scheme = uri.protocol;
    // const slashes = uri.slashes;
    const hostname = uri.hostname;
    const pathname = uri.pathname;
    if (scheme !== 'http:' && scheme !== 'https:') {
      //|| !slashes
      return false;
    }
    // Validate hostname: Can contain letters, numbers, underscore and dashes separated by a dot.
    // Each zone must not start with a hyphen or underscore.
    if (
      !hostname ||
      !/^[a-zA-Z0-9]+[\w-]*([.]?[a-zA-Z0-9]+[\w-]*)*$/.test(hostname)
    ) {
      return false;
    }
    // Allow for pathnames: (/chars+)*/?
    // Where chars can be a combination of: a-z A-Z 0-9 - _ . ~ ! $ & ' ( ) * + , ; = : @ %
    const pathnameRe = /^(\/[\w\-.~!$'()*+,;=:@%]+)*\/?$/;
    // Validate pathname.
    if (pathname && pathname !== '/' && !pathnameRe.test(pathname)) {
      return false;
    }
    // Allow any query string and hash as long as no invalid character is used.
  } catch (e) {
    return false;
  }
  return true;
}

export function createDownloadFile(data: Blob, linkName: string) {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;

  link.setAttribute('download', linkName); //or any other extension
  document.body.appendChild(link);
  link.click();
}

export function createPdf(data: Blob, fileName: string) {
  return createFile(data, fileName, 'pdf');
}

export function createFile(
  data: Blob,
  fileName: string,
  fileExtension?: string
) {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  let linkName = `${fileName}`;

  if (fileExtension) {
    linkName = linkName.concat(`.${fileExtension}`);
  }

  link.href = url;

  link.setAttribute('download', linkName); //or any other extension
  document.body.appendChild(link);
  link.click();
  URL.revokeObjectURL(link.href);
}

// The regular expression used in this example checks if the string starts with the
//data: protocol followed by a MIME type (e.g. image/png, text/html, etc.) and any number
//of optional parameters separated by semicolons. It then checks if the string includes the base64 encoding
//suffix and a valid Base64-encoded string.
export function isValidDataUrl(str: string): boolean {
  const regex = /^data:(.*);base64,(.*)$/;
  return regex.test(str);
}

// This function converts a base64 string into a File object
export function convertDataURlToFile(dataURL: string, filename: string) {
  const matches = dataURL.match(/^data:(.*);base64,(.*)$/);
  if (!matches || matches.length !== 3) {
    throw new Error('Invalid data URL');
  }
  const [, mimeType, base64] = matches;
  const byteString = atob(base64);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  const blob = new Blob([ab], { type: mimeType });
  return new File([blob], filename);
}

// This function converts a base64 string into a File object
export function convertBase64ToFile(base64: string, filename: string) {
  const byteString = atob(base64);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  const blob = new Blob([ab], { type: 'application/octet-stream' });
  return new File([blob], filename);
}

export function downloadDataUrl(dataUrl: string, filename: string) {
  const link = document.createElement('a');
  link.download = filename;
  link.href = dataUrl;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function getInitials(name: string) {
  return name
    .toUpperCase()
    .split(' ')
    .map(n => n[0])
    .join('');
}

export function waitForElementToLoad(
  elementId: string,
  maxAttempts = 10,
  interval = 1000
): Promise<HTMLElement | null> {
  return new Promise<HTMLElement | null>((resolve, reject) => {
    let attempts = 0;

    function checkElement() {
      const element = document.getElementById(elementId);

      if (element) {
        // Element found, resolve the promise
        resolve(element);
      } else {
        attempts++;

        if (attempts >= maxAttempts) {
          // Max attempts reached, reject the promise
          reject(new Error(`Element with id "${elementId}" not found`));
        } else {
          // Continue checking
          setTimeout(checkElement, interval);
        }
      }
    }

    checkElement(); // Start checking immediately
  });
}

const parser = new DOMParser();

export function isHtmlTextEmpty(html = '') {
  const { textContent } = parser.parseFromString(
    html,
    'text/html'
  ).documentElement;

  // if there is text make sure its not just whitespace
  return textContent ? !textContent.trim() : true;
}

export function blobToBase64(blob: Blob) {
  const reader = new FileReader();
  reader.readAsDataURL(blob);
  return new Promise<string>(resolve => {
    reader.onloadend = () => {
      resolve(reader.result as string);
    };
  });
}

export function setErrorFromAxiosError(error: AxiosError) {
  const systemErrorData: ISystemErrorData = {
    name: error.name,
    message: error.message
  };
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    systemErrorData.message = error.response.statusText;
    if (error.response.data.detail) {
      systemErrorData.detail = error.response.data.detail;
    }
    // Check for field errors - exclude 'detail' key to check if other keys contain field errors
    const dataWithoutDetail = omit(error.response.data, 'detail');
    if (FieldErrors.isFieldError(dataWithoutDetail)) {
      systemErrorData.fieldErrors = new FieldErrors(dataWithoutDetail);
    }
  } else if (error.request) {
    // The request was made but no response was received (Can happen on 500 error)
    systemErrorData.detail =
      'There was an error in processing your request. Please check with IT Admin';
  }
  return new SystemError(systemErrorData);
}

//By default axios converts list parameters to 'key[]=value'... this is not compatible with django.
//this is why we use URLSearchParams to convert the list to 'key=value'
export function convertFilterToAxiosParams(filter: any) {
  return new URLSearchParams(
    Object.entries({
      ...filter
    }).reduce((acc, [key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(val => acc.append(key, String(val)));
      } else {
        acc.append(
          key,
          typeof value == 'boolean' || value ? String(value) : ''
        );
      }
      return acc;
    }, new URLSearchParams())
  );
}

export function convertBooleanStringToNullableBoolean(
  value: string | boolean
): boolean | null {
  if (typeof value === 'boolean') return value;
  let booleanParam: boolean | null = null;
  if (value === 'false') {
    booleanParam = false;
  } else if (value === 'true') {
    booleanParam = true;
  }

  return booleanParam;
}

export function convertObjectIdsToNumbers<T extends { id?: number }>(
  objectIdList: Array<number | T>
): number[] {
  return objectIdList.map(objectId => {
    if (typeof objectId === 'number') {
      return objectId;
    }
    // If objectId is an object, return its id if defined, otherwise return itself
    return getPropertyFromItem(objectId, 'id', objectId);
  });
}

export function extractTextFromHtml(htmlString: string): string {
  if (!htmlString) return '';
  // Remove script and style tags and their content
  const withoutScripts = htmlString.replace(
    /<script[^>]*>([\s\S]*?)<\/script>/gi,
    ''
  );
  const withoutStyles = withoutScripts.replace(
    /<style[^>]*>([\s\S]*?)<\/style>/gi,
    ''
  );
  // Remove all HTML tags
  const text = withoutStyles.replace(/<\/?[^>]+(>|$)/g, '');
  // Convert HTML entities to plain text
  const temp = document.createElement('span');
  temp.innerHTML = text;
  const out = temp.textContent || temp.innerText || '';
  return out.trim();
}
